import Message, { IMessageProps } from 'atom/Message';
import * as React from 'react';
import { v4 } from 'uuid';

interface IShowToastOptions {
  message?: string;
  description?: string;
  variant: IMessageProps['variant'];
  urgency?: IMessageProps['urgency'];
  onDismiss?: () => void;
  autoHide?: number;
}

export interface ToasterContainerRef {
  showToast: (options: IShowToastOptions) => void;
  showError: (message: string, description?: string) => void;
  showSuccess: (message: string, description?: string) => void;
}

export const DEFAULT_TOASTER: ToasterContainerRef = {
  showToast: () => {},
  showError: () => {},
  showSuccess: () => {}
};

const TITLE = {
  alert: 'Error',
  notice: 'Warning',
  info: 'Info',
  success: 'Success'
} as const;

const ToasterContainer = React.forwardRef<ToasterContainerRef>((props, ref) => {
  const [toasts, setToasts] = React.useState<(IMessageProps & { key: string })[]>([]);

  const showToast: ToasterContainerRef['showToast'] = ({ message, onDismiss, ...rest }) => {
    const key = v4();
    const newToast: IMessageProps = {
      ...rest,
      className: 'tw:pointer-events-auto',
      message: message ?? TITLE[rest.variant ?? 'info'],
      onClose: () => {
        setToasts(v => v.filter(t => t.key !== key));
        onDismiss?.();
      }
    };
    setToasts([...toasts, { ...newToast, key }]);
  };

  const showError: ToasterContainerRef['showError'] = (message: string, description?: string) => {
    showToast({
      message: description != null ? message : TITLE.alert,
      description: description ?? message,
      variant: 'alert',
      urgency: 'important'
    });
  };

  const showSuccess: ToasterContainerRef['showSuccess'] = (message: string, description?: string) => {
    showToast({
      message: description != null ? message : TITLE.success,
      description: description ?? message,
      variant: 'success',
      autoHide: 3000
    });
  };

  React.useImperativeHandle(ref, () => ({
    showToast,
    showError,
    showSuccess
  }));

  return (
    <React.Fragment>
      <div
        className="toast-container tw:fixed tw:bottom-2 tw:right-2 tw:flex tw:flex-col tw:gap-2"
        style={{ zIndex: 3000 }}
      >
        {toasts.map(({ key, ...toast }) => (
          <Message key={key} className="tw:max-w-md" {...toast} />
        ))}
      </div>
    </React.Fragment>
  );
});

export default ToasterContainer;
